import { ClipboardContents, ScreenshotDisplay, ScreenshotWindow } from '@renderer/chat/types'
import { FocusedWindow, HighlightContext, AppWindow } from '@highlight/common'
import OverlayIpcRouter, {
  type OverlayIpcRouterType,
  type RequestContextParams,
} from '@shared/ipc/routers/OverlayIpc.router'
import { SelectableContext } from '@shared/types/SelectableContext'
import type { OverlayFindInPageRequest } from '@shared/sidepanel/find-in-page'

class OverlayIpcRenderer extends OverlayIpcRouter implements OverlayIpcRouterType {
  async requestContext(params: RequestContextParams): Promise<HighlightContext | undefined> {
    return this.exec('requestContext', params)
  }

  async requestContextForWindow(options: { window: AppWindow | FocusedWindow }): Promise<SelectableContext> {
    return this.exec('requestContextForWindow', options)
  }

  async getClipboardContents(): Promise<ClipboardContents | undefined> {
    return this.exec('getClipboardContents')
  }

  async screenshotWindow(windowTitle: string): Promise<string> {
    return this.exec('screenshotWindow', windowTitle)
  }

  async updateContextMacro(): Promise<void> {
    return this.exec('updateContextMacro')
  }

  async compressBase64ImageToJpeg({
    base64Image,
    quality = 80,
  }: {
    base64Image: string
    quality?: number
  }): Promise<string> {
    return this.exec('compressBase64ImageToJpeg', { base64Image, quality })
  }

  async compressBase64ImageToPng({
    base64Image,
    quality = 80,
  }: {
    base64Image: string
    quality?: number
  }): Promise<string> {
    return this.exec('compressBase64ImageToPng', { base64Image, quality })
  }

  // @ts-ignore @todo fix type!
  async writeTextToClipboard(text: string, html?: string): Promise<void> {
    return this.exec('writeTextToClipboard', { text, html })
  }

  async cancelOngoingContextRequest(): Promise<void> {
    return this.exec('cancelOngoingContextRequest')
  }

  async typeText(options: { text: string; pid?: number; method?: 'paste' | 'type'; html?: string }): Promise<void> {
    return await this.exec('typeText', options)
  }
  async requestFocusedElementAttributes(): Promise<string> {
    return this.exec('requestFocusedElementAttributes')
  }

  async focusHighlightWindow(): Promise<void> {
    return this.exec('focusHighlightWindow')
  }

  async focusWindow(options: { title: string; pid?: number }): Promise<boolean | void> {
    return this.exec('focusWindow', options)
  }

  async getScreenshotWindows(includeAllWindows?: boolean): Promise<ScreenshotWindow[]> {
    return this.exec('getScreenshotWindows', includeAllWindows)
  }

  async getDisplayScreenshots(): Promise<ScreenshotDisplay[]> {
    return this.exec('getDisplayScreenshots')
  }

  async getActiveDisplayScreenshot(): Promise<string | undefined> {
    return this.exec('getActiveDisplayScreenshot')
  }

  async toggleWindowClickThrough(clickThrough: boolean) {
    return this.exec('toggleWindowClickThrough', clickThrough)
  }

  async getAppIcon(appName: string): Promise<string | null> {
    return this.exec('getAppIcon', appName)
  }

  async requestTextAboveFocusedElement(): Promise<string> {
    return this.exec('requestTextAboveFocusedElement')
  }

  async getMousePosition(): Promise<Electron.Point> {
    return this.exec('getMousePosition')
  }

  async muteNotificationForHours(options: { ipcKey: string; hours: number }): Promise<void> {
    return this.exec('muteNotificationForHours', options)
  }

  async muteNotificationPermanently(ipcKey: string): Promise<void> {
    return this.exec('muteNotificationPermanently', ipcKey)
  }

  async unmuteNotification(ipcKey: string): Promise<void> {
    return this.exec('unmuteNotification', ipcKey)
  }

  async closeSidepanel(): Promise<void> {
    return this.exec('closeSidepanel')
  }

  async findInPage(request: OverlayFindInPageRequest): Promise<boolean> {
    return this.exec('findInPage', request)
  }

  async stopFindInPage(): Promise<void> {
    return this.exec('stopFindInPage')
  }
}

export default new OverlayIpcRenderer()
